import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BadgeCheckIcon, LockIcon, MessageSquareIcon, ShieldIcon, UsersIcon } from 'lucide-react';
import { Panel } from '../ui/Panel';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { ChoiceGroup } from '../ui/ChoiceGroup';
import { ReportControls } from '../trust/ReportControls';
import { RecommendationPanel } from '../intelligence/RecommendationPanel';
import { useAccount } from '../../contexts/AccountContext';
import { useSpaceAccess } from '../../hooks/useSpaceAccess';
import { recommendGroups } from '../../utils/recommend';
import { groups as seedGroups } from '../../data/community';
import { organizations } from '../../data/campus';
import type { CommunityGroup } from '../../types/community';
const FILTERS = ['All', 'Course', 'Club', 'Association', 'Housing', 'Interest'] as const;
export function GroupsPanel() {
  const [groups, setGroups] = useState<CommunityGroup[]>(seedGroups);
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>('All');
  const {
    signals
  } = useAccount();
  const suggestions = recommendGroups(signals);
  function toggleJoin(id: string) {
    setGroups(current => current.map(group => group.id === id ? {
      ...group,
      joined: !group.joined,
      members: group.members + (group.joined ? -1 : 1)
    } : group));
  }
  const visible = groups.filter(group => filter === 'All' || group.kind === filter);
  return <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]" data-id="element-802" data-mp="2dg34v53z58rk">
      <div className="space-y-4" data-id="element-803" data-mp="2dhe9161c0ij7">
        <ChoiceGroup options={FILTERS} value={filter} onChange={setFilter} size="sm" data-id="element-804" data-mp="1zoyektmhxstw" />
        <ul className="space-y-3" data-id="element-805" data-mp="23pi8lobrwgbt">
          {visible.map(group => <GroupRow key={group.id} group={group} onToggle={() => toggleJoin(group.id)} data-id="element-806" data-mp="23ndyupr70n3m" />)}
        </ul>
      </div>

      <div className="space-y-6" data-id="element-807" data-mp="2w6kxl9ot44cx">
      <RecommendationPanel title="Suggested for you" description="Drawn from your registered courses and campus." items={suggestions.map(suggestion => ({
        id: suggestion.item.id,
        title: suggestion.item.name,
        meta: `${suggestion.item.members.toLocaleString()} members · ${suggestion.item.privacy}`,
        reason: suggestion.reason,
        fallback: suggestion.fallback
      }))} data-id="element-808" data-mp="2w6unkx9uqbim" />

      <Panel title="Recognised organizations" description="Official bodies with a Campuzon page." bodyClassName="p-0" data-id="element-809" data-mp="2n2xdh3egawcw">
        <ul className="divide-y divide-line" data-id="element-810" data-mp="2ic78hr9xsviq">
          {organizations.map(organization => <li key={organization.id} className="px-5 py-3.5" data-id="element-811" data-mp="2ea2hl0aviczs">
              <p className="text-[13px] font-semibold text-ink" data-id="element-812" data-mp="2e8hlh68wulzt">{organization.name}</p>
              <p className="cz-num mt-0.5 text-[12px] text-ink-muted" data-id="element-813" data-mp="299o8rg1g6z0p">
                {organization.members.toLocaleString()} members · {organization.category}
              </p>
              <p className="mt-1 text-[12px] leading-5 text-ink-muted" data-id="element-814" data-mp="1ze4r9ynms2vh">{organization.description}</p>
              {organization.joined && <Badge tone="ok" className="mt-1.5" data-id="element-815" data-mp="1u8r4drcux1ja">
                  Member
                </Badge>}
            </li>)}
        </ul>
        <div className="border-t border-line px-5 py-3" data-id="element-816" data-mp="1z8dhm0soqhs3">
          <Link to="/community" data-id="element-817" data-mp="247gntokeiu1n">
            <Button size="sm" variant="ghost" data-id="element-818" data-mp="245m062hqwntv">
              Organizations publish events too
            </Button>
          </Link>
        </div>
      </Panel>
      </div>
    </div>;
}

/**
 * One group. Membership requirements are enforced here from the configured
 * policy rather than left to a moderator to check by hand.
 */
function GroupRow({
  group,
  onToggle
}: {
  group: CommunityGroup;
  onToggle: () => void;
}) {
  const {
    policy,
    decision,
    allowed
  } = useSpaceAccess(group.id);
  const inviteOnly = group.privacy === 'Invite only';
  const blocked = !group.joined && (inviteOnly || !allowed);
  return <li className="rounded-card border border-line bg-surface px-5 py-4" data-id="element-819" data-mp="spxcf8wo3ks0">
              <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-820" data-mp="2uhh3gahlm30v">
                <div className="min-w-0" data-id="element-821" data-mp="2piw1o6mp7bab">
                  <div className="flex flex-wrap items-center gap-2" data-id="element-822" data-mp="2pfrud0dxrzff">
                    <h3 className="text-[15px] font-semibold text-ink" data-id="element-823" data-mp="348bqvi6kz8nw">{group.name}</h3>
                    <Badge data-id="element-824" data-mp="2zf8uz3ebzn4j">{group.kind}</Badge>
                    {group.privacy !== 'Open' && <Badge tone="neutral" icon={<LockIcon className="h-3.5 w-3.5" data-id="element-826" data-mp="3d5m61emirhy3" />} data-id="element-825" data-mp="3d6484rvv4qdx">
                        {group.privacy}
                      </Badge>}
                    {policy && <Badge tone={allowed ? 'ok' : 'warn'} icon={<BadgeCheckIcon className="h-4 w-4" data-id="element-828" data-mp="3i7thm33fsvzw" />} data-id="element-827" data-mp="3n538ln6h5fpu">
                        {allowed ? 'You meet the requirement' : 'Verification required'}
                      </Badge>}
                  </div>
                  <p className="mt-1.5 text-[13px] leading-6 text-ink-muted" data-id="element-829" data-mp="3agk98ycfaal">{group.description}</p>
                  <p className="cz-num mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-ink-subtle" data-id="element-830" data-mp="899vsvu2kr84">
                    <span className="inline-flex items-center gap-1.5" data-id="element-831" data-mp="3pqt44hm733nh">
                      <UsersIcon className="h-3 w-3" data-id="element-832" data-mp="3up4cenwud63l" />
                      {group.members.toLocaleString()} members
                    </span>
                    <span className="inline-flex items-center gap-1.5" data-id="element-833" data-mp="3gojt7qn0l89k">
                      <MessageSquareIcon className="h-3 w-3" data-id="element-834" data-mp="3loz66h3dwdr0" />
                      {group.activity}
                    </span>
                    <span className="inline-flex items-center gap-1.5" data-id="element-835" data-mp="36sgrglw2rbn5">
                      <ShieldIcon className="h-3 w-3" data-id="element-836" data-mp="3bpqho3rafrc1" />
                      {group.moderatedBy}
                    </span>
                  </p>
                </div>
                <Button size="sm" variant={group.joined ? 'secondary' : 'primary'} onClick={onToggle} disabled={blocked} data-id="element-837" data-mp="14xzeuphfnpvo">
                  {group.joined ? 'Leave' : inviteOnly ? 'Invite only' : !allowed ? 'Requirement not met' : group.privacy === 'Request to join' ? 'Request to join' : 'Join'}
                </Button>
              </div>

              {policy && !allowed && decision && <div className="mt-3 rounded-md border border-line bg-canvas p-4" data-id="element-838" data-mp="1m9o6lh55ef65">
                  <p className="text-body font-semibold text-ink" data-id="element-839" data-mp="1hhcwjlgp1czv">{policy.requirement}</p>
                  <p className="mt-1 max-w-[72ch] text-body-sm text-ink" data-id="element-840" data-mp="1dhm47dbiqot8">{decision.message}</p>
                  {decision.remedy && <p className="mt-1.5 max-w-[72ch] text-body-sm text-ink-muted" data-id="element-841" data-mp="2a00b0i16ptrz">
                      {decision.remedy}
                    </p>}
                  <Link to="/identity" className="mt-3 inline-block" data-id="element-842" data-mp="3nvs5hq15wigr">
                    <Button size="sm" variant="secondary" data-id="element-843" data-mp="3stjvgei8xuib">
                      Verify your academic identity
                    </Button>
                  </Link>
                </div>}

              <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-line pt-3" data-id="element-844" data-mp="5ovmc4y96jyb">
                <p className="text-[12px] text-ink-subtle" data-id="element-845" data-mp="ap05y3ugjusr">
                  {group.kind === 'Housing' ? 'Residents only. Shared costs raised here appear in Money as splits.' : 'Messages are moderated by the group owner and the Campuzon trust queue.'}
                </p>
                <ReportControls subject={group.name} surface="group" data-id="element-846" data-mp="oek7j6caym0h" />
              </div>
            </li>;
}